/**
 * BookingPriceSection - Display and manage booking price
 */
import React from 'react';
import { Button } from '../../ui/button';
import { Loader2, CreditCard, RotateCcw, Save, CheckCircle2 } from 'lucide-react';
import { formatDate } from './constants';

const BookingPriceSection = ({
  booking,
  isCancelled,
  isClosed,
  hasChanges,
  newPrice,
  priceLoading,
  saveLoading,
  paymentLoading,
  onCalculatePrice,
  onSaveModifications,
  onResetChanges,
  onPayByCard
}) => {
  // Price calculations
  const currentPrice = booking.price?.total_ttc || booking.price?.total || 0;
  const amountPaid = booking.amount_paid || booking.price?.amount_paid || 0;
  const amountRefunded = booking.amount_refunded || booking.price?.amount_refunded || 0;
  const netPaid = amountPaid - amountRefunded;
  const displayPrice = newPrice ? newPrice.total : currentPrice;
  const balanceDue = displayPrice - netPaid;
  const priceDifference = newPrice ? (newPrice.total - currentPrice) : 0;
  
  // For cancelled bookings: calculate what we owe to the client
  const amountToRefund = netPaid;

  if (isCancelled) {
    return (
      <div className="rounded-lg p-4 bg-red-500/10 border border-red-500/30">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[#E5E5E5]/50 text-xs uppercase">Résumé financier</p>
          <span className="text-red-400 text-xs font-medium">Réservation annulée</span>
        </div>
        
        <div className="space-y-2 text-sm">
          {/* Original price */}
          <div className="flex justify-between">
            <span className="text-[#E5E5E5]/50">Prix initial</span>
            <span className="text-[#E5E5E5]/50 line-through">{currentPrice.toFixed(2)}€</span>
          </div>
          
          {amountPaid > 0 ? (
            <>
              <div className="flex justify-between">
                <span className="text-[#E5E5E5]/50">Montant payé</span>
                <span className="text-green-400 font-medium">{amountPaid.toFixed(2)}€</span>
              </div>
              
              {amountRefunded > 0 ? (
                <>
                  <div className="flex justify-between items-center py-2 border-t border-[#C9A24D]/10">
                    <span className="text-blue-400 font-medium flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Remboursé
                    </span>
                    <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-sm font-bold">
                      {amountRefunded.toFixed(2)}€
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-[#C9A24D]/10">
                    <span className="text-[#E5E5E5] font-medium">Total dû</span>
                    <span className="text-green-400 font-bold text-lg">0.00€</span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between items-center pt-2 border-t border-orange-500/30 mt-2">
                  <span className="text-orange-400 font-medium">À vous rembourser</span>
                  <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-sm font-bold">
                    {amountToRefund.toFixed(2)}€
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="flex justify-between pt-2 border-t border-[#C9A24D]/10">
              <span className="text-[#E5E5E5] font-medium">Total dû</span>
              <span className="text-green-400 font-bold text-lg">0.00€</span>
            </div>
          )}
        </div>
        
        {/* Cancellation info */}
        {booking.cancellation_request && (
          <div className="mt-3 pt-3 border-t border-red-500/20 text-xs text-[#E5E5E5]/40">
            <p>Annulée le {formatDate(booking.cancellation_request.processed_at || booking.cancellation_request.requested_at)}</p>
            {booking.cancellation_request.reason && (
              <p className="mt-1">Raison : {booking.cancellation_request.reason}</p>
            )}
          </div>
        )}
      </div>
    );
  }

  // Active booking - Normal price display
  return (
    <div className="rounded-lg p-3 sm:p-4 bg-gradient-to-r from-[#C9A24D]/20 to-[#C9A24D]/5 border border-[#C9A24D]/30">
      <div className="flex items-center justify-between mb-2 sm:mb-3">
        <div>
          <p className="text-[#E5E5E5]/50 text-[10px] sm:text-xs uppercase">Prix</p>
          {hasChanges && !newPrice && (
            <p className="text-orange-400 text-[10px] sm:text-xs">Modifications en attente</p>
          )}
        </div>
        <div className="text-right">
          {newPrice ? (
            <div>
              <p className="text-[#E5E5E5]/50 text-[10px] sm:text-xs line-through">{currentPrice.toFixed(2)}€</p>
              <p className="text-[#C9A24D] text-xl sm:text-2xl font-bold">{newPrice.total.toFixed(2)}€</p>
              {priceDifference !== 0 && (
                <p className={`text-[10px] sm:text-xs ${priceDifference > 0 ? 'text-orange-400' : 'text-green-400'}`}>
                  {priceDifference > 0 ? '+' : ''}{priceDifference.toFixed(2)}€
                </p>
              )}
            </div>
          ) : (
            <p className="text-[#C9A24D] text-xl sm:text-2xl font-bold">{currentPrice.toFixed(2)}€</p>
          )}
        </div>
      </div>

      {/* Amount paid / Refunded / Balance */}
      {(amountPaid > 0 || amountRefunded > 0) && (
        <div className="pt-2 border-t border-[#C9A24D]/20 space-y-1 text-xs sm:text-sm">
          <div className="flex justify-between">
            <span className="text-[#E5E5E5]/50">Total payé</span>
            <span className="text-green-400 font-medium">{amountPaid.toFixed(2)}€</span>
          </div>
          {amountRefunded > 0 && (
            <div className="flex justify-between">
              <span className="text-[#E5E5E5]/50">Remboursé</span>
              <span className="text-blue-400 font-medium">-{amountRefunded.toFixed(2)}€</span>
            </div>
          )}
          {amountRefunded > 0 && (
            <div className="flex justify-between border-t border-[#C9A24D]/10 pt-1">
              <span className="text-[#E5E5E5]/70 font-medium">Net payé</span>
              <span className="text-[#E5E5E5] font-medium">{netPaid.toFixed(2)}€</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-[#E5E5E5]/50">Reste à payer</span>
            <span className={`font-bold ${balanceDue > 0 ? 'text-orange-400' : 'text-green-400'}`}>
              {Math.max(0, balanceDue).toFixed(2)}€
            </span>
          </div>
        </div>
      )}

      {/* Calculate / Save buttons */}
      {hasChanges && (
        <div className="mt-3 space-y-2">
          {!newPrice ? (
            <Button
              onClick={onCalculatePrice}
              disabled={priceLoading}
              className="w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B]"
              data-testid="calculate-price-btn"
            >
              {priceLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
              Recalculer le tarif
            </Button>
          ) : (
            <div className="space-y-2">
              <Button
                onClick={onSaveModifications}
                disabled={saveLoading}
                className="w-full bg-green-600 hover:bg-green-500 text-white"
                data-testid="save-modifications-btn"
              >
                {saveLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Confirmer les modifications
              </Button>
              <Button
                onClick={onResetChanges}
                variant="ghost"
                className="w-full text-[#E5E5E5]/60"
              >
                Annuler les modifications
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Payment button */}
      {!hasChanges && balanceDue > 0 && !isClosed && (
        <Button
          onClick={onPayByCard}
          disabled={paymentLoading}
          className="w-full mt-3 bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B]"
          data-testid="pay-by-card-btn"
        >
          {paymentLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
          {amountPaid > 0 ? `Régler le solde (${balanceDue.toFixed(2)}€)` : `Payer (${displayPrice.toFixed(2)}€)`}
        </Button>
      )}
    </div>
  );
};

export default BookingPriceSection;
